import { Component, inject, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { MatIconModule } from '@angular/material/icon';
import { CommonModule } from '@angular/common';
import { MatTooltipModule } from '@angular/material/tooltip';
import { finalize } from 'rxjs';
import { HasPermissionDirective } from '@olv/ui-shared/directives/has-permission.directive';
import { NetworkMetadataService } from '@olv/ui-shared/services/network-metadata.service';
import { RiskLevelEnum } from '@olv/ui-shared/enums/task.enums';
import { PagedResultDto } from '@olv/ui-shared/models/paged-result.dto';
import { Resources, Scopes } from '@olv/ui-shared/models/settlement-permissions.model';
import { WalletTypeEnum, WalletStatusEnum, WalletFilterParams } from '@olv/ui-shared/models/wallet.model';
import { DatePickerService, PresetPeriod, RangeOfDate } from '@olv/ui-shared/services/date-picker.service';
import { FilterService, AppliedFilter } from '@olv/ui-shared/services/filter.service';
import { MerchantService } from '@olv/ui-shared/services/merchant.service';
import { ReportsService, WalletReport } from '@olv/ui-shared/services/reports.service';
import { DatePickerComponent } from '@olv/ui-shared/ui-shared/date-picker/date-picker.component';
import { PillSearchComponent, SearchState } from '@olv/ui-shared/ui-shared/pill-search/pill-search.component';
import { ShowMoreComponent } from '@olv/ui-shared/ui-shared/show-more/show-more.component';
import { enumToFilterFields } from '@olv/ui-shared/utils/enum-to-filter.util';
import { shortenAddress } from '@olv/ui-shared/utils/shortenAddress.utils';
import { LoadingSpinnerComponent } from '@olv/ui-shared/ui-shared/loading-spinner/loading-spinner.component';
import { AppContextService } from '@olv/ui-shared/services/app-context.service';
import { StateBadgeComponent } from '@olv/ui-shared/ui-shared/state-badge/state-badge.component';
import { MatDialog } from '@angular/material/dialog';
import { FiltersDialogComponent } from '@olv/ui-shared/ui-shared/filters-dialog/filters.component';
import { ExportComponent, ExportDialogData } from '@olv/ui-shared/ui-shared/export/export.component';
import { FormatCoinCurrencyPipe } from '@olv/ui-shared/pipes/format-coin-currency.pipe';
import { AuthService } from '@olv/ui-shared/services/auth.service';
import { EmptyStateComponent } from "@olv/ui-shared/ui-shared/empty-state/empty-state.component";
import { ButtonComponent } from '@olv/ui-shared/ui-shared/button/button.component';
@Component({
  selector: 'app-reports-wallets',
  standalone: true,
  imports: [
    MatIconModule,
    CommonModule,
    MatTooltipModule,
    PillSearchComponent,
    HasPermissionDirective,
    DatePickerComponent,
    ShowMoreComponent,
    LoadingSpinnerComponent,
    StateBadgeComponent,
    FormatCoinCurrencyPipe,
    EmptyStateComponent,
    ButtonComponent
],
  templateUrl: './reports-wallets.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./reports-wallets.component.scss'],
})
export class ReportsWalletsComponent implements OnInit {
  protected readonly Resources = Resources;
  protected readonly Scopes = Scopes;
  protected readonly shortenAddress = shortenAddress;

  WalletTypeEnum = WalletTypeEnum;
  RiskLevelEnum = RiskLevelEnum;
  WalletStatusEnum = WalletStatusEnum;

  private reportsService = inject(ReportsService);
  private datePickerService = inject(DatePickerService);
  private dialog = inject(MatDialog);
  private auth = inject(AuthService);
  private readonly metadata = inject(NetworkMetadataService);

  wallets: WalletReport[] = [];
  totalCount = 0;
  skipCount = 0;
  pageSize = 10;
  baseCurrencySymbol: string | null = null;

  initialLoading = true;
  private walletsLoaded = false;
  private merchantsLoaded = true;
  private shouldLoadMerchants = false;
  isLoading = false;
  isLoadingMore = false;
  isFirstLoad = true;
  isDisableFilters = false;
  merchantsLoading = false;
  error: string | null = null;
  hasExportData = false;

  filterMap: WalletFilterParams = {};

  filtersApplied = false;

  searchText = '';

  isSettlementApp = false;

  selectedPeriod = this.datePickerService.DEFAULT_DATE_PERIOD;

  get filters() {
    return [
      { name: 'Risk Level', filterFields: enumToFilterFields(RiskLevelEnum) },
      { name: 'Blockchain', filterFields: this.metadata.filterFields() },
      { name: 'Wallet Type', filterFields: enumToFilterFields(WalletTypeEnum) },
      { name: 'Status', filterFields: enumToFilterFields(WalletStatusEnum) },
      {
        name: 'Balance',
        filterFields: [
          { name: 'Balance Above Zero (>0)', value: 1 },
          { name: 'Zero Balance (=0)', value: 0 },
        ],
      },
    ];
  }

  merchantOptions: { value: string; label: string }[] = [];

  private merchantService = inject(MerchantService);
  private filterService = inject(FilterService);
  private readonly appCtx = inject(AppContextService);

  ngOnInit() {
    this.metadata.load();
    const appName = this.appCtx.getAppName();
    this.isSettlementApp = appName === 'settlement';
    this.initializeComponent();
  }

  private initializeComponent(): void {
    const { start, end } = this.datePickerService.setDefaultDate();
    this.filterMap.startDate = start.toISOString();
    this.filterMap.endDate = end.toISOString();

    this.initialLoading = true;
    this.walletsLoaded = false;
    this.merchantsLoaded = false;

    this.shouldLoadMerchants = this.auth.hasPermission(Resources.Reports, Scopes.MerchantsFilter);

    this.loadWallets();

    if (this.shouldLoadMerchants) {
      this.loadMerchants();
    } else {
      this.merchantsLoaded = true;
    }
  }

  private resetPagination(): void {
    this.skipCount = 0;
    this.wallets = [];
  }

  private resolveInitialLoading(): void {
    if (this.walletsLoaded && this.merchantsLoaded) {
      this.initialLoading = false;
    }
  }

  loadMerchants(): void {
    this.merchantsLoading = true;

    this.reportsService
      .getMerchantFilter()
      .pipe(
        finalize(() => {
          this.merchantsLoading = false;
          this.merchantsLoaded = true;
          this.resolveInitialLoading();
        }),
      )
      .subscribe({
        next: (merchants) => {
          this.merchantOptions = merchants.map((m) => ({
            value: m.id,
            label: m.name,
          }));
        },
      });
  }

  loadWallets(): void {
    this.isLoading = true;
    this.error = null;

    const params: WalletFilterParams = {
      ...this.filterMap,
      filter: this.searchText?.trim() || undefined,
      skipCount: this.skipCount,
      maxResultCount: this.pageSize,
    };

    this.reportsService
      .getReportWallets(params)
      .pipe(
        finalize(() => {
          this.isLoading = false;
          this.isLoadingMore = false;

          if (!this.walletsLoaded) {
            this.walletsLoaded = true;
            this.resolveInitialLoading();
          }
        }),
      )
      .subscribe({
        next: (res: PagedResultDto<WalletReport>) => {
          if (this.skipCount === 0) {
            this.wallets = res.items;
            this.baseCurrencySymbol = res.items[0]?.baseCurrencySymbol ?? '$';
          } else {
            this.wallets = [...this.wallets, ...res.items];
          }

          this.totalCount = res.totalCount;
          this.hasExportData = this.wallets.length > 0;

          if (this.isFirstLoad) {
            this.isDisableFilters = this.wallets.length === 0 && !this.searchText?.trim();
          }
          this.isFirstLoad = false;
        },
        error: () => {
          this.error = 'Failed to load wallets';
          this.hasExportData = false;
          this.resetPagination();
        },
      });
  }

  loadMoreWallets(): void {
    if (this.isLoadingMore || !this.hasMoreWallets) {
      return;
    }

    this.isLoadingMore = true;
    this.skipCount += this.pageSize;
    this.loadWallets();
  }

  updateSearch(searchState: SearchState): void {
    if (this.searchText === searchState.value) {
      return;
    }

    this.searchText = searchState.value;
    this.resetPagination();
    this.loadWallets();
  }

  onPresetPeriod(preset: string): void {
    const range = this.datePickerService.presetPeriod(preset as PresetPeriod);
    this.selectedPeriod = this.datePickerService.selectedPeriod;
    this.filterMap.startDate = range.start.toISOString();
    this.filterMap.endDate = range.end.toISOString();

    this.resetPagination();
    this.loadWallets();
  }

  onDateRangeSelected(range: RangeOfDate): void {
    const newRange = this.datePickerService.dateRangeSelected(range);
    this.selectedPeriod = this.datePickerService.selectedPeriod;
    this.filterMap.startDate = newRange.start.toISOString();
    this.filterMap.endDate = newRange.end.toISOString();

    this.resetPagination();
    this.loadWallets();
  }

  getAppliedFiltersAsArray() {
    return this.filterService.getAppliedFilters(this.filterMap);
  }
  onFilterReset(): void {
    this.filtersApplied = false;

    const { startDate, endDate } = this.filterMap;

    this.filterMap = {
      startDate: startDate,
      endDate: endDate,
    };

    this.resetPagination();
    this.loadWallets();
  }

  onFiltersApplied(values: AppliedFilter[]): void {
    this.filtersApplied = values.length > 0;

    const { startDate, endDate } = this.filterMap;

    this.filterMap = {
      startDate: startDate,
      endDate: endDate,
      ...this.filterService.applySelectedFilters(values, {}),
    };

    this.resetPagination();
    this.loadWallets();
  }

  toggleFilters(): void {
    this.metadata.whenLoaded().subscribe(() => this.openFilters());
  }

  private openFilters(): void {
    const dialogRef = this.dialog.open(FiltersDialogComponent, {
      maxWidth: '95vw',
      maxHeight: '95vh',
      disableClose: false,
      autoFocus: false,
      panelClass: 'custom-dialog-container',
      data: {
        filters: this.filters,
        dropdownsOptions: [{
          key: 'merchantIds',
          label: 'Merchants',
          placeholder: 'Select Merchants',
          options: this.merchantOptions,
          type: 'checkbox',
          isFilterClass: true,
          isSearch: true,
        }],
        preselectedValues: this.getAppliedFiltersAsArray(),
        isDisableFilters: this.isDisabled,
      },
    });

    const sub = dialogRef.componentInstance.resetRequest.subscribe((result) => {
      this.onFiltersApplied(result);
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (result) {
        this.onFiltersApplied(result);
      }

      sub.unsubscribe();
    });
  }

  openExport(): void {
    this.dialog.open<ExportComponent, ExportDialogData>(ExportComponent, {
      disableClose: false,
      autoFocus: false,
      maxWidth: '95vw',
      maxHeight: '95vh',
      panelClass: 'custom-dialog-container',
      data: {
        tabName: 'wallets',
        walletFilters: {
          startDate: this.filterMap.startDate,
          endDate: this.filterMap.endDate,
          filter: this.filterMap.filter,
          merchantIds: this.filterMap.merchantIds,
          networkTypes: this.filterMap.blockchain,
          riskLevels: this.filterMap.riskLevel,
          walletTypes: this.filterMap.walletType,
          walletStatuses: this.filterMap.status as number[],
          walletBalance: this.filterMap.balance,
          systemWalletTypes: this.filterMap.systemWalletType,
        },
      },
    });
  }

  get isDisabled(): boolean {
    if (this.initialLoading) {
      return true;
    }

    return this.isDisableFilters;
  }

  get exportButtonState(): 'disabled' | 'default' {
    return this.isLoading || !this.hasExportData ? 'disabled' : 'default';
  }
  get hasWallets(): boolean {
    return this.wallets && this.wallets.length > 0;
  }

  get hasMoreWallets(): boolean {
    return this.wallets.length < this.totalCount;
  }
}
